import countries from 'i18n-iso-countries'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChevronLeft } from 'Assets/icons/ChevronLeft'
import { useBMStore } from '../../../../stores/useBMStore'

import {
  AdvancedFiltersState,
  FilterCategory,
  ThreatGroupOptions,
  ThreatTypesOptions,
} from './AdvancedFilters.models'
import {
  ActionButton,
  FiltersContainer,
  FilterSection,
  OptionsWrapper,
  CheckboxItem,
  FilterTitleText,
  FiltersHeader,
  IconButtonStyled,
  StyledCheckBox,
} from './AdvancedFilters.styled'

interface AdvancedFiltersProps {
  onApplyFilters: (filters: AdvancedFiltersState) => void
}

export const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({ onApplyFilters }) => {
  const { t } = useTranslation('botMitigation')
  const domains = useBMStore((state) => state.domains)
  const filters = useBMStore((state) => state.advancedFilters)

  const [viewMode, setViewMode] = useState<'categories' | 'options'>('categories')
  const [expandedCategory, setExpandedCategory] = useState<FilterCategory | null>(null)

  const [filterState, setFilterState] = useState<AdvancedFiltersState>({
    domains: filters?.domains || [],
    threatGroups: filters?.threatGroups || [],
    countries: filters?.countries || [],
    threatTypes: filters?.threatTypes || [],
  })

  const domainOptions = domains.map((domain) => ({ value: domain.address, label: domain.address }))
  const countryOptions = Object.entries(countries.getNames('en', { select: 'alias' })).map(
    ([code, name]) => ({
      value: code,
      label: name,
    })
  )

  const toggleSelection = (array: string[], value: string): string[] => {
    return array.includes(value) ? array.filter((item) => item !== value) : [...array, value]
  }

  const handleToggle = (filterType: keyof AdvancedFiltersState, value: string) => {
    setFilterState((prev) => ({ ...prev, [filterType]: toggleSelection(prev[filterType], value) }))
  }

  const toggleCategory = (category: FilterCategory) => {
    setExpandedCategory(category)
    setViewMode('options')
  }

  const goBackToCategories = () => {
    setViewMode('categories')
    setExpandedCategory(null)
    onApplyFilters(filterState)
  }

  useEffect(() => {
    // Trigger animation on viewMode change
  }, [viewMode])

  return (
    <FiltersContainer>
      <FiltersHeader>
        {viewMode === 'options' && (
          <IconButtonStyled onClick={goBackToCategories}>
            <ChevronLeft />
          </IconButtonStyled>
        )}
        {t('advancedFilters.header')}
      </FiltersHeader>
      {viewMode === 'categories' ? (
        <>
          <FilterSection onClick={() => toggleCategory('domains')}>
            <FilterTitleText>{t('advancedFilters.domains')}</FilterTitleText>
          </FilterSection>
          <FilterSection onClick={() => toggleCategory('threatGroups')}>
            <FilterTitleText>{t('advancedFilters.threatGroups')}</FilterTitleText>
          </FilterSection>
          <FilterSection onClick={() => toggleCategory('countries')}>
            <FilterTitleText>{t('advancedFilters.countries')}</FilterTitleText>
          </FilterSection>
          <FilterSection onClick={() => toggleCategory('threatTypes')}>
            <FilterTitleText>{t('advancedFilters.threatTypes')}</FilterTitleText>
          </FilterSection>
        </>
      ) : (
        <>
          <OptionsWrapper>
            {(expandedCategory === 'domains'
              ? domainOptions
              : expandedCategory === 'threatGroups'
              ? ThreatGroupOptions
              : expandedCategory === 'countries'
              ? countryOptions
              : ThreatTypesOptions
            ).map((option, index) => (
              <CheckboxItem key={index}>
                {expandedCategory && filterState[expandedCategory] && (
                  <StyledCheckBox
                    label={option.label}
                    isChecked={filterState[expandedCategory]?.includes(option.value) ?? false}
                    onChange={() =>
                      handleToggle(expandedCategory as keyof AdvancedFiltersState, option.value)
                    }
                  />
                )}
              </CheckboxItem>
            ))}
          </OptionsWrapper>
          <ActionButton onClick={goBackToCategories}>{t('advancedFilters.add')}</ActionButton>
        </>
      )}
    </FiltersContainer>
  )
}
